import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {isArray, noop} from 'ramda-adjunct';

import {
  useDeleteLabourUnitMutation,
  usePostLabourUnitActiveMutation,
  usePostLabourUnitInactiveMutation,
  useSetLabourUnitAsDefaultMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

export function WorkshopUnits() {
  const navigate = useNavigate();

  const [activateLabourUnit] = usePostLabourUnitActiveMutation();
  const [deactivateLabourUnit] = usePostLabourUnitInactiveMutation();
  const [deleteLabourUnit] = useDeleteLabourUnitMutation();
  const [setLabourUnitAsDefault] = useSetLabourUnitAsDefaultMutation();

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? rowId[0] : rowId;

    match(actionKey)
      .with('redirectDetail', 'edit', () => {
        navigate(composePath(settingsRoutes.workshopUnitDetail, {params: {id}}));
      })
      .with('setAsDefault', () => {
        setLabourUnitAsDefault({labourUnitId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setActive', () => {
        activateLabourUnit({labourUnitId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('setInactive', () => {
        deactivateLabourUnit({labourUnitId: id})
          .unwrap()
          .then(() => refreshData())
          .catch(handleApiError);
      })
      .with('delete', () => {
        openDeleteDialog({
          onConfirm: () => {
            deleteLabourUnit({labourUnitId: id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError);
          },
        });
      })
      .otherwise(noop);
  };

  const header: SettingsTemplateHeader = {
    title: i18n.t('entity.workshop.labels.units'),
    actions: [
      {
        type: 'button',
        title: i18n.t('entity.workshop.actions.newUnit'),
        onClick: () => navigate(settingsRoutes.workshopUnitCreate),
      },
    ],
  };

  return (
    <>
      <Helmet title={i18n.t('entity.workshop.labels.units')} />
      <SettingsTemplate data-testid={testIds.settings.workshopUnits('template')} header={header}>
        <DmsDataGrid gridCode="labour-unit" actionCallback={actionCallback} />
      </SettingsTemplate>
    </>
  );
}
